import React, { useState } from 'react';
import { Button, Dialog, LabeledContainer } from '@schibsted-svp/react-ui';

import SortableList from '../SortableList/SortableList';

import { PresetItem } from './PresetItem/PresetItem';
import { CategoriesSearch } from './CategoriesSearch/CategoriesSearch';
import { AssetsSearch } from './AssetsSearch/AssetsSearch';
import { ProfilesSearch } from './ProfilesSearch/ProfilesSearch';
import { StoriesSearch } from './StoriesSearch/StoriesSearch';

import type {
  ComponentResponse,
  EnrichedTag,
  PresetElement,
  SvpAsset,
  SvpCategory,
} from '../../types';
import type { FC } from 'react';

import styles from './PresetPicker.module.scss';

type Props = {
  dataType: ComponentResponse['data']['type'];
  presetItems: PresetElement[];
  singleItem?: boolean;
  onChange: (preset: PresetElement[]) => void;
};

export const PresetPicker: FC<Props> = ({
  dataType,
  presetItems = [],
  onChange,
  singleItem = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const preset = presetItems;

  function onEditButtonClick() {
    setIsOpen(true);
  }

  function onClearButtonClick() {
    onChange([]);
  }

  function onDialogClose() {
    setIsOpen(false);
  }

  function onRemoveClick(item: PresetElement) {
    onChange(preset.filter(({ id }) => id !== item.id));
  }

  function onAddClick(item: PresetElement) {
    if (singleItem) {
      onChange([item]);
    } else {
      onChange([...preset, item]);
    }
  }

  const buttonLabel = `${preset.length > 0 ? 'Edit' : 'Choose'} ${dataType} ${
    preset.length === 0 ? 'manually' : ''
  }`;

  return (
    <LabeledContainer label="Data Preset">
      <div className={styles.container}>
        {preset.length > 0 && (
          <SortableList
            className={styles.sortableList}
            compact
            items={preset}
            renderItem={(item: PresetElement) => (
              <PresetItem
                item={item}
                dataType={dataType}
                isSelected
                isDraggable
                onClick={onRemoveClick}
              />
            )}
            onChange={onChange}
            keyProperty={'id'}
          />
        )}
        <div className={styles.buttons}>
          <Button
            size="small"
            type="button"
            variant="danger"
            onClick={onClearButtonClick}
            disabled={preset.length === 0}
          >
            Remove all
          </Button>
          <Button
            size="small"
            type="button"
            variant="standard"
            onClick={onEditButtonClick}
          >
            {buttonLabel}
          </Button>
        </div>
        <Dialog isOpen={isOpen} onClose={onDialogClose} heading={buttonLabel}>
          <Dialog.Section>
            {(dataType === 'categories' || dataType === 'mixed') && (
              <CategoriesSearch
                selectedCategories={preset as SvpCategory[]}
                onAddClick={onAddClick}
                onRemoveClick={onRemoveClick}
                label={
                  dataType === 'mixed'
                    ? 'Search by title or ID (categories)'
                    : undefined
                }
              />
            )}
            {(dataType === 'assets' || dataType === 'mixed') && (
              <AssetsSearch
                selectedAssets={preset as SvpAsset[]}
                onAddClick={onAddClick}
                onRemoveClick={onRemoveClick}
                label={
                  dataType === 'mixed'
                    ? 'Search by title or ID (assets)'
                    : undefined
                }
              />
            )}
            {dataType === 'profiles' && (
              <ProfilesSearch
                selectedProfiles={preset as EnrichedTag[]}
                onRemoveClick={onRemoveClick}
                onAddClick={onAddClick}
              />
            )}
            {dataType === 'stories' && (
              <StoriesSearch
                selectedStories={preset as SvpAsset[]}
                onRemoveClick={onRemoveClick}
                onAddClick={onAddClick}
              />
            )}
          </Dialog.Section>
          <Dialog.Section variant="darker" mode="flexRight">
            <Button type="button" onClick={onDialogClose} variant="standard">
              Close
            </Button>
          </Dialog.Section>
        </Dialog>
      </div>
    </LabeledContainer>
  );
};
